import React from 'react';
import dynamic from 'next/dynamic';
import { AgencyInfoSection } from '../../AgencyInfoSection';
import { OwnerInfoSection } from '../../OwnerInfoSection';
import { ResidenceAdditionalInfo } from '../../ResidenceAdditionalInfo';
import { SpaceSection } from '../../../SpaceSection';
import useScreenSize from '../../../../../hooks/useScreenSize';
import { PhoneButtonClickHandler } from '../../../BottomPhoneButton/types';
import { AlertMessage } from '../../AlertMessage';
import { AgencyAdditionalInfo } from '../../AgencyAdditionalInfo';
import { AgencyInfoWrapper } from './styled';
import { CurrentItemQuery } from '../../../../../graphql/graphql';
import { useClient } from '../../../../../hooks/useClient';
import { DeskBlock } from '../../CurrentItemSections/styled';
import { PlaceholderBlock } from '../../../../../styled/placeholders.styled';

interface OwnerContactsBlockProps {
  isAgency: boolean;
  item: CurrentItemQuery['item'];
  isPublished: boolean;
  isAuthor: boolean;
  isResidence: boolean;
  maskedPhones?: string[];
  onClickHandler: PhoneButtonClickHandler;
  phonesShown: string[] | null;
  isVisible?: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const PhonesSection = dynamic(
  () => import('../../PhonesSection').then(mod => mod.PhonesSection),
  {
    ssr: false
  }
);

const BottomPhoneButton = dynamic(
  () => import('../../../BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false,
    loading: () => <PlaceholderBlock height="60px" margin="0" />
  }
);

export const OwnerContactsBlock: React.FC<OwnerContactsBlockProps> = ({
  isAgency,
  item,
  isPublished,
  isAuthor,
  isResidence,
  maskedPhones,
  onClickHandler,
  phonesShown,
  isVisible,
  setIsVisible
}) => {
  const { isLarge, isSmallDesktop } = useScreenSize();
  const isClient = useClient();

  const ownerName = isResidence
    ? item?.business?.companyName
    : item?.contactName;
  const ownership = isResidence ? null : item?.contactTypeName;
  const logoUrl = isResidence ? item?.business?.logoUrl : null;
  const showOnDesk = ((isLarge || isSmallDesktop) && isClient) || !isClient;

  const shouldRenderPhoneButtonOnDesktop =
    showOnDesk && (isPublished || isAuthor);

  const agencyInfo = (
    <AgencyInfoSection
      path={item?.business?.path || ''}
      name={item?.business?.name}
      imageUrl={item?.business?.logoUrl}
    />
  );

  return (
    <SpaceSection>
      {isAgency && !showOnDesk && (
        <AgencyInfoWrapper>{agencyInfo}</AgencyInfoWrapper>
      )}

      <OwnerInfoSection
        ownerName={ownerName}
        ownership={ownership}
        logoUrl={logoUrl}
        isAgency={isAgency}
        isResidence={isResidence}
      />

      {shouldRenderPhoneButtonOnDesktop ? (
        <DeskBlock>
          <BottomPhoneButton
            stat="product-call-btn"
            phones={isAuthor ? maskedPhones : phonesShown}
            clickHandler={onClickHandler}
            {...{ setIsVisible, isVisible, isAuthor }}
          />
        </DeskBlock>
      ) : (
        <PhonesSection
          phonesMasked={maskedPhones}
          onClickHandler={onClickHandler}
          phonesShown={isAuthor ? maskedPhones : phonesShown}
          isAuthor={isAuthor}
        />
      )}

      {!isAgency && !isResidence && !isAuthor && !!phonesShown?.length && (
        <AlertMessage />
      )}

      {isPublished && isAgency && showOnDesk && (
        <AgencyAdditionalInfo
          linkPath={item?.business?.path}
          agencyInfo={agencyInfo}
          offersCount={item?.business?.itemsCount}
          schedule={item?.business?.schedule}
          address={item?.business?.contactAddress}
          latitude={item?.business?.latitude}
          longitude={item?.business?.longitude}
          city={item?.business?.city?.name}
        />
      )}

      {isPublished && isResidence && (
        <ResidenceAdditionalInfo
          linkPath={item?.business?.path}
          website={item?.business?.website}
          contactEmail={item?.business?.contactEmail}
          schedule={item?.business?.schedule}
          address={item?.business?.address}
          latitude={item?.business?.lat}
          longitude={item?.business?.lng}
          apartments={item?.business?.apartments}
          priceMin={item?.business?.price?.total?.minimum}
          currency={item?.business?.price?.currency}
          deadline={item?.business?.deadline}
          hasBillOfSale={item?.business?.hasBillOfSale}
          city={item?.business?.city?.name}
        />
      )}
    </SpaceSection>
  );
};
